import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, List, ListItemButton, ListItemText, Snackbar, Stack,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AxiosContext } from "../../context/AxiosContext";
import { BsContainer, BsRow, BsCol } from "../../layouts/components";
import { moduleApi, newObjectId, isObjectId } from "./moduleApi";

// Gestion de modulos y procesos.
//
// Es el catalogo que alimenta los checkboxes de «Privilegios de Usuario»: sin
// esta pantalla, crear un modulo o un proceso solo era posible escribiendo
// directo en Mongo.
//
// Dos cosas que la UI tiene que respetar, y por eso estan explicitas:
//
//  1. `update-module` REEMPLAZA el arreglo `process` entero. Se envia siempre
//     la lista completa que se ve en pantalla.
//  2. `create-module` exige que el id lo genere el cliente. Se puede escribir
//     a mano —util cuando el panel ya espera un id concreto— o generar uno.
//
// Y una limitacion que conviene conocer: renombrar aqui NO actualiza a los
// usuarios que ya tengan el modulo asignado, porque `Users.moduleProcess[]`
// guarda copias denormalizadas. El id es lo unico que manda.

const vacio = () => ({ id: "", name: "", namePrint: "", process: [] });

const ModuleManagement = () => {
  const axiosContext = useContext(AxiosContext);

  const [modulos, setModulos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);

  const [seleccionado, setSeleccionado] = useState(null);
  const [borrador, setBorrador] = useState(null);
  const [esNuevo, setEsNuevo] = useState(false);
  const [dialogoProceso, setDialogoProceso] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    moduleApi.list(axiosContext, {
      onSuccess: (data) => setModulos(Array.isArray(data) ? data : []),
      onError: setError,
      onFinally: () => setCargando(false),
    });
  }, [axiosContext]);

  useEffect(() => cargar(), [cargar]);

  const abrir = (m) => {
    setSeleccionado(m.id);
    setEsNuevo(false);
    setBorrador({
      id: m.id,
      name: m.name || "",
      namePrint: m.namePrint || "",
      process: (m.process || []).map((p) => ({ ...p })),
    });
  };

  const nuevo = () => {
    setSeleccionado(null);
    setEsNuevo(true);
    setBorrador({ ...vacio(), id: newObjectId() });
  };

  const guardar = () => {
    if (!borrador.name || !borrador.namePrint) {
      setError("Nombre y nombre visible son obligatorios.");
      return;
    }
    if (!isObjectId(borrador.id)) {
      setError("El id debe ser un ObjectId válido: 24 caracteres hexadecimales.");
      return;
    }
    const idsMal = borrador.process.filter((p) => !isObjectId(p.id));
    if (idsMal.length) {
      setError(`Hay ${idsMal.length} proceso(s) con id inválido.`);
      return;
    }

    // Se envia SIEMPRE la lista completa de procesos: el backend la reemplaza.
    const body = {
      id: borrador.id,
      name: borrador.name,
      namePrint: borrador.namePrint,
      process: borrador.process.map((p) => ({
        id: p.id,
        name: p.name,
        namePrint: p.namePrint,
        idModule: borrador.id,
      })),
    };

    const cb = {
      onSuccess: () => {
        setAviso(esNuevo ? "Módulo creado." : "Módulo guardado.");
        setBorrador(null);
        setEsNuevo(false);
        cargar();
      },
      onError: setError,
    };

    if (esNuevo) moduleApi.create(axiosContext, body, cb);
    else moduleApi.update(axiosContext, body, cb);
  };

  const agregarProceso = (proceso) => {
    setBorrador((b) => ({ ...b, process: [...b.process, proceso] }));
    setDialogoProceso(null);
  };

  const quitarProceso = (id) =>
    setBorrador((b) => ({ ...b, process: b.process.filter((p) => p.id !== id) }));

  // Renombrar un proceso existente. El `id` no se toca: es lo unico que mira
  // el panel para decidir permisos, y los usuarios ya asignados guardan una
  // copia denormalizada del nombre que este cambio no actualiza.
  const editarProceso = (id, campo, valor) =>
    setBorrador((b) => ({
      ...b,
      process: b.process.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)),
    }));

  const ordenados = useMemo(
    () => [...modulos].sort((a, b) => String(a.namePrint).localeCompare(String(b.namePrint), "es")),
    [modulos]
  );

  return (
    <div className="form-style">
      <BsContainer fluid class="px-0 px-md-3">
        <BsRow class="no-gutters">
          <BsCol class="col-md-12">
            <Typography variant="h5" gutterBottom>
              Módulos y procesos
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Este catálogo alimenta los checkboxes de «Privilegios de Usuario». Renombrar aquí
              no actualiza a los usuarios que ya tengan el módulo asignado: el id es lo que manda.
            </Typography>

            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ my: 2 }}>
                {error}
              </Alert>
            )}

            <BsRow>
              <BsCol class="col-md-4">
                <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                        Módulos ({ordenados.length})
                      </Typography>
                      <Button size="small" startIcon={<AddIcon />} onClick={nuevo}>
                        Nuevo
                      </Button>
                    </Stack>
                    {cargando ? (
                      <Typography variant="caption" color="text.secondary">
                        Cargando…
                      </Typography>
                    ) : (
                      <List dense sx={{ maxHeight: 520, overflowY: "auto" }}>
                        {ordenados.map((m) => (
                          <ListItemButton
                            key={m.id}
                            selected={seleccionado === m.id}
                            onClick={() => abrir(m)}
                          >
                            <ListItemText
                              primary={m.namePrint}
                              secondary={`${m.name} · ${(m.process || []).length} proceso(s)`}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </BsCol>

              <BsCol class="col-md-8">
                {borrador ? (
                  <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        {esNuevo ? "Nuevo módulo" : "Editar módulo"}
                      </Typography>

                      <Stack spacing={2} mt={1}>
                        <TextField
                          size="small"
                          label="Id (ObjectId)"
                          value={borrador.id}
                          onChange={(e) => setBorrador({ ...borrador, id: e.target.value.trim() })}
                          disabled={!esNuevo}
                          error={!isObjectId(borrador.id)}
                          helperText={
                            esNuevo
                              ? "Se puede escribir a mano si el panel ya espera un id concreto."
                              : "El id no se puede cambiar: los usuarios asignados apuntan a él."
                          }
                          InputProps={{
                            endAdornment: esNuevo && (
                              <Tooltip title="Generar otro">
                                <IconButton
                                  size="small"
                                  onClick={() => setBorrador({ ...borrador, id: newObjectId() })}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ),
                          }}
                        />
                        <TextField
                          size="small"
                          label="Nombre interno"
                          value={borrador.name}
                          onChange={(e) => setBorrador({ ...borrador, name: e.target.value })}
                        />
                        <TextField
                          size="small"
                          label="Nombre visible"
                          value={borrador.namePrint}
                          onChange={(e) => setBorrador({ ...borrador, namePrint: e.target.value })}
                        />
                      </Stack>

                      <Stack direction="row" alignItems="center" mt={3} mb={1}>
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                          Procesos ({borrador.process.length})
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            setDialogoProceso({ id: newObjectId(), name: "", namePrint: "" })
                          }
                        >
                          Agregar proceso
                        </Button>
                      </Stack>

                      {borrador.process.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Sin procesos. Un módulo sin procesos se puede asignar, pero no ofrece
                          nada que marcar en «Privilegios de Usuario».
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          {borrador.process.map((p) => (
                            <Stack
                              key={p.id}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}
                            >
                              <Box flexGrow={1}>
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    size="small"
                                    label="Nombre visible"
                                    value={p.namePrint || ""}
                                    onChange={(e) => editarProceso(p.id, "namePrint", e.target.value)}
                                    helperText="Es la etiqueta del checkbox en «Privilegios de Usuario»"
                                    fullWidth
                                  />
                                  <TextField
                                    size="small"
                                    label="Nombre interno"
                                    value={p.name || ""}
                                    onChange={(e) => editarProceso(p.id, "name", e.target.value)}
                                    fullWidth
                                  />
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                  id: {p.id}
                                </Typography>
                              </Box>
                              {!isObjectId(p.id) && (
                                <Chip size="small" color="error" label="id inválido" />
                              )}
                              <IconButton size="small" onClick={() => quitarProceso(p.id)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ))}
                        </Stack>
                      )}

                      <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                        Al guardar, la lista de procesos <b>reemplaza</b> por completo a la
                        anterior. Los procesos que quites dejarán de existir en el catálogo.
                      </Alert>

                      <Stack direction="row" spacing={1} mt={2}>
                        <Button variant="contained" onClick={guardar}>
                          Guardar
                        </Button>
                        <Button onClick={() => { setBorrador(null); setEsNuevo(false); }}>
                          Cancelar
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                    Selecciona un módulo de la lista o crea uno nuevo.
                  </Typography>
                )}
              </BsCol>
            </BsRow>
          </BsCol>
        </BsRow>
      </BsContainer>

      <Dialog open={Boolean(dialogoProceso)} onClose={() => setDialogoProceso(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo proceso</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              size="small"
              label="Id (ObjectId)"
              value={dialogoProceso?.id || ""}
              onChange={(e) => setDialogoProceso({ ...dialogoProceso, id: e.target.value.trim() })}
              error={!isObjectId(dialogoProceso?.id)}
              helperText="Se puede escribir a mano si el panel ya espera un id acordado."
            />
            <TextField
              size="small"
              label="Nombre interno"
              value={dialogoProceso?.name || ""}
              onChange={(e) => setDialogoProceso({ ...dialogoProceso, name: e.target.value })}
            />
            <TextField
              size="small"
              label="Nombre visible"
              value={dialogoProceso?.namePrint || ""}
              onChange={(e) => setDialogoProceso({ ...dialogoProceso, namePrint: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoProceso(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              !dialogoProceso?.name ||
              !dialogoProceso?.namePrint ||
              !isObjectId(dialogoProceso?.id)
            }
            onClick={() => agregarProceso(dialogoProceso)}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(aviso)}
        autoHideDuration={4000}
        onClose={() => setAviso(null)}
        message={aviso}
      />
    </div>
  );
};

export default ModuleManagement;
